import { useState } from 'react';

import { useCollapsibleSection } from 'shared/hooks/collapsibleSections';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useUpdateLookerUrlMutation } from '../services/projects.api';
import { LookerIntegration } from '../types/integrations';

interface Props {
  data: LookerIntegration;
  projectId: number;
}

const LookerSection = ({ data, projectId }: Props) => {
  const sectionProps = useCollapsibleSection('looker');
  const { connected, url } = data;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(url ?? '');
  const [updateLookerUrl, { isLoading }] = useUpdateLookerUrlMutation();

  const handleSave = async () => {
    await updateLookerUrl({ projectId, lookerUrl: draft });
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(url ?? '');
    setEditing(false);
  };

  return (
    <Accordion
      type="single"
      collapsible
      {...sectionProps}
      className="bg-bg-surface border border-border-subtle rounded-xl"
    >
      <AccordionItem value="content" className="border-none">
        <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-bg-surface-raised rounded-xl data-[state=open]:rounded-b-none">
          <span className="flex items-center gap-3">
            <span className="font-semibold text-text-default">Looker</span>
            <Badge className={connected ? 'bg-success-light text-success' : 'bg-neutral-100 text-text-subtle'}>
              {connected ? 'Conectado' : 'No conectado'}
            </Badge>
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-5 pb-5 border-t border-border-subtle">
          <div className="flex flex-col gap-3 pt-4">
            {editing ? (
              <div className="flex flex-col gap-2">
                <Label
                  htmlFor="looker-url"
                  className="text-xs font-semibold uppercase tracking-widest text-text-subtle"
                >
                  Dashboard URL
                  <Input
                    id="looker-url"
                    type="url"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="https://lidz.cloud.looker.com/..."
                    className="mt-1.5 normal-case font-normal"
                  />
                </Label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={isLoading}
                    onClick={() => {
                      void handleSave();
                    }}
                  >
                    {isLoading ? 'Guardando...' : 'Guardar'}
                  </Button>
                  <Button size="sm" variant="outline" disabled={isLoading} onClick={handleCancel}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {connected && url ? (
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-semibold uppercase tracking-widest text-text-subtle">Dashboard URL</p>
                    <p className="text-sm text-text-muted break-all">{url}</p>
                  </div>
                ) : (
                  <p className="text-sm text-text-muted">Looker no está configurado para este proyecto.</p>
                )}
                <div className="flex gap-2">
                  {connected && url && (
                    <Button size="sm" asChild>
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        Abrir
                      </a>
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setDraft(url ?? '');
                      setEditing(true);
                    }}
                  >
                    {connected ? 'Editar URL' : 'Configurar URL'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default LookerSection;
